/**
 * The one button in the application (task 9.2).
 *
 * Every variant is at least 44x44 (WCAG 2.5.5), carries the shared focus ring,
 * and takes a `data-testid` — the Playwright agent navigates by accessible name
 * and test id, so an unlabelled control is a broken test as well as a broken
 * screen (design.md D15).
 */
import type { ButtonHTMLAttributes, JSX, Ref } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg';

const BASE =
  'touch-target inline-flex items-center justify-center gap-2 rounded-lg border font-medium ' +
  'transition-colors select-none disabled:cursor-not-allowed disabled:opacity-55';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'border-transparent bg-accent text-accent-ink hover:bg-accent-hover',
  secondary: 'border-line-strong bg-surface text-ink hover:bg-surface-muted',
  ghost: 'border-transparent bg-transparent text-ink-muted hover:bg-surface-muted hover:text-ink',
  danger: 'border-transparent bg-danger text-surface hover:opacity-90',
};

const SIZES: Record<ButtonSize, string> = {
  md: 'px-4 py-2 text-sm',
  lg: 'w-full px-5 py-3 text-base',
};

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md'): string {
  return `${BASE} ${VARIANTS[variant]} ${SIZES[size]}`;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant | undefined;
  readonly size?: ButtonSize | undefined;
  readonly ref?: Ref<HTMLButtonElement> | undefined;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  className = '',
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  return <button type={type} className={`${buttonClass(variant, size)} ${className}`} {...rest} />;
}

export interface ButtonLinkProps extends LinkProps {
  readonly variant?: ButtonVariant | undefined;
  readonly size?: ButtonSize | undefined;
}

/** A route link that reads as a button. Still a link: it navigates. */
export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  className = '',
  ...rest
}: ButtonLinkProps): JSX.Element {
  return <Link className={`${buttonClass(variant, size)} no-underline ${className}`} {...rest} />;
}
